import { useEffect, useState, type FormEvent } from 'react';
import { ApiError } from '../lib/api.js';
import {
  changedRoles,
  draftFrom,
  groupByModule,
  toggled,
  permissionLabel,
  sortRoles,
  useCreateRole,
  useDeleteRole,
  useRenameRole,
  useRoleMatrix,
  useSaveRoles,
  type Draft,
  type MatrixView,
  type RoleView,
} from '../lib/roles.js';

/* ============================================================================
   /settings/roles — the permission matrix, editable. 1.4c
   ----------------------------------------------------------------------------
   Roles have been rows since 1.4a and the API has been able to edit them since
   1.4b. This is the grid, and two things about it are not decoration:

   **The owner column is locked, and so are the seven owner-only rows.** They
   render as a tick or a dash with no checkbox at all — not as a disabled one,
   and never as an empty one. An empty checkbox says "you may grant this", and
   those seven permissions are refused by ROLE as well as by permission, so the
   owner would tick it, save, and watch the account still get 403.
   CONTEXT.md §14 q11.

   **The draft is local until Save.** Every tick edits a draft; Save sends one
   PUT per CHANGED role, carrying that role's whole set. Nothing is written as
   you tick, because a matrix that saves per checkbox is 28 rows of chances to
   leave a role halfway between two intentions.
   ========================================================================== */

export function RolesPage() {
  const { data, isPending, error } = useRoleMatrix();
  const save = useSaveRoles();

  /** Null until the matrix arrives, and null again after a clean save. */
  const [draft, setDraft] = useState<Draft | null>(null);

  useEffect(() => {
    /* Only when there is no draft, so a refetch cannot overwrite ticks that
       have not been saved yet — the same rule the invoice details follow. */
    if (data && draft === null) setDraft(draftFrom(data));
  }, [data, draft]);

  function toggle(roleKey: string, permission: string) {
    /* The last save's verdict stops being about what is on screen the moment
       something else is ticked. Leaving "Saved: Sales." above a changed box
       is the same lie as a stale WARNING. */
    if (save.data || save.error) save.reset();
    if (!data) return;
    /* `toggled` handles a null draft rather than ignoring the tick — see its
       comment; a click in that window used to vanish. */
    setDraft((current) => toggled(current, data, roleKey, permission));
  }

  function onSave() {
    if (!data || !draft) return;
    const changes = changedRoles(data, draft).map((key) => ({
      key,
      permissions: draft[key] ?? [],
    }));
    /* The draft is NOT reset here, and that is the fix for a bug the
       cross-origin walk found: resetting it rebuilt it from whatever the
       query cache held at that instant, which is the PRE-save matrix — so the
       screen went on saying "Not saved: Sales." about a change that had
       already landed, and the refetch could no longer correct it because a
       non-null draft is never re-synced. Keeping it is also simply true: the
       draft IS what was just sent. `changedRoles` empties itself the moment
       the refetch agrees, and a role that was REFUSED stays changed, which is
       exactly what should happen to it. */
    save.mutate(changes);
  }

  if (isPending) {
    return (
      <>
        <Head />
        <p className="muted">Loading…</p>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <Head />
        <p className="alert" role="alert">
          {error instanceof ApiError ? error.message : 'Could not load the roles.'}
        </p>
      </>
    );
  }

  const roles = sortRoles(data.roles);
  const changed = draft ? changedRoles(data, draft) : [];
  const labelOf = (key: string) => roles.find((role) => role.key === key)?.label ?? key;

  return (
    <>
      <Head />

      <div className="stack">
        <RoleList roles={roles} />

        <section className="card">
          <h2 className="section-title">What each role may do</h2>

          <p className="muted" style={{ margin: '0 0 4px' }}>
            A change takes effect on that person&rsquo;s very next click — there is no cache and
            nobody has to sign in again.
          </p>
          <p className="muted" style={{ margin: '0 0 12px' }}>
            The Owner column is locked, and so are the {data.ownerOnly.length} rows marked owner
            only. Those are refused by role as well as by permission, so a tick beside one would
            promise something the API still answers 403 to.
          </p>

          <Matrix
            matrix={data}
            roles={roles}
            draft={draft}
            onToggle={toggle}
            busy={save.isPending}
          />

          <Outcome outcome={save.data ?? null} error={save.error} labelOf={labelOf} />

          <div className="row" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn"
              onClick={onSave}
              disabled={save.isPending || changed.length === 0}
            >
              {save.isPending ? 'Saving…' : 'Save changes'}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                /* Rebuilt from the data in hand rather than set to null, so
                   nothing has to re-sync it later — see the note on the save
                   above for what re-syncing cost. */
                setDraft(draftFrom(data));
                save.reset();
              }}
              disabled={save.isPending || changed.length === 0}
            >
              Discard
            </button>
            <span className="muted" role="status">
              {changed.length === 0
                ? 'Nothing changed yet.'
                : `Not saved: ${changed.map(labelOf).join(', ')}.`}
            </span>
          </div>
        </section>

        <AddRole />
      </div>
    </>
  );
}

function Head() {
  return (
    <div className="page-head">
      <div>
        <h1>Roles</h1>
        <p className="muted">What each role may do. Everyone on a role gets the change.</p>
      </div>
    </div>
  );
}

/* --- the roles themselves -------------------------------------------------- */

/**
 * The list above the grid, where a role is named, renamed and deleted.
 *
 * The member count is here rather than only in the error: deleting a role
 * somebody is on is refused, and "3 accounts" beside it says so before the
 * click rather than after.
 */
function RoleList({ roles }: { roles: RoleView[] }) {
  return (
    <section className="card">
      <h2 className="section-title">Roles</h2>
      <div className="stack">
        {roles.map((role) => (
          <RoleRow key={role.key} role={role} />
        ))}
      </div>
    </section>
  );
}

function RoleRow({ role }: { role: RoleView }) {
  const rename = useRenameRole();
  const remove = useDeleteRole();
  const [label, setLabel] = useState(role.label);

  const members = `${role.members} ${role.members === 1 ? 'account' : 'accounts'}`;
  /* `owner` is not editable at all, built-ins keep their names, and a role
     with accounts on it cannot be deleted. All three are enforced in the
     service — this only stops offering what would be refused. */
  const editable = !role.builtIn;
  const deletable = editable && role.members === 0;

  return (
    <div className="lead-card">
      <div className="lead-card__business">{role.label}</div>
      <div className="lead-card__meta">
        <code>{role.key}</code> ·{' '}
        <span className={role.builtIn ? 'pill pill--muted' : 'pill'}>
          {role.builtIn ? 'Built-in' : 'Custom'}
        </span>{' '}
        · {members}
        {role.key === 'owner' ? ' · locked' : ''}
      </div>

      {editable ? (
        <div className="row" style={{ marginTop: 8 }}>
          <label className="row" style={{ gap: 6, alignItems: 'center' }}>
            <span className="muted">Name</span>
            <input
              aria-label={`Name for ${role.key}`}
              value={label}
              maxLength={60}
              onChange={(event) => setLabel(event.target.value)}
            />
          </label>
          <button
            type="button"
            className="btn btn--ghost btn--small"
            disabled={rename.isPending || label.trim() === '' || label.trim() === role.label}
            onClick={() => rename.mutate({ key: role.key, label: label.trim() })}
          >
            {rename.isPending ? 'Saving…' : 'Save name'}
          </button>

          {deletable ? (
            <button
              type="button"
              className="btn btn--ghost btn--small"
              disabled={remove.isPending}
              onClick={() => remove.mutate(role.key)}
            >
              {remove.isPending ? 'Deleting…' : 'Delete role'}
            </button>
          ) : (
            <span className="muted">
              {members} on this role — move them off it before it can be deleted.
            </span>
          )}
        </div>
      ) : null}

      {rename.error || remove.error ? (
        <p className="field__error" role="alert">
          {(rename.error ?? remove.error) instanceof ApiError
            ? ((rename.error ?? remove.error) as ApiError).message
            : 'That did not work.'}
        </p>
      ) : null}
    </div>
  );
}

/* --- the grid -------------------------------------------------------------- */

function Matrix({
  matrix,
  roles,
  draft,
  onToggle,
  busy,
}: {
  matrix: MatrixView;
  roles: RoleView[];
  draft: Draft | null;
  onToggle: (roleKey: string, permission: string) => void;
  busy: boolean;
}) {
  const ownerOnly = new Set<string>(matrix.ownerOnly);
  const groups = groupByModule(matrix.permissions);

  return (
    <div className="matrix-scroll">
      <table className="matrix">
        <caption className="sr-only">
          Permissions down the side, roles across the top. A ticked box means that role holds that
          permission.
        </caption>
        <thead>
          <tr>
            <th scope="col" className="matrix__what">
              Permission
            </th>
            {roles.map((role) => (
              <th scope="col" key={role.key}>
                {role.label}
                {/* A bare number under a column heading is a number about
                    nothing. It says what it counts. */}
                <span className="matrix__sub">
                  {role.key === 'owner'
                    ? 'locked'
                    : `${role.members} ${role.members === 1 ? 'account' : 'accounts'}`}
                </span>
              </th>
            ))}
          </tr>
        </thead>

        {groups.map((group, index) => (
          /* The module in the key is not unique on its own — groupByModule starts
             a new group whenever the module changes, so a list that ever
             interleaves two modules yields two groups with the same name. */
          <tbody key={`${group.module}-${index}`}>
            <tr className="matrix__group">
              <th scope="rowgroup" colSpan={roles.length + 1}>
                {group.label}
              </th>
            </tr>
            {group.permissions.map((permission) => {
              const locked = ownerOnly.has(permission);
              return (
                <tr key={permission}>
                  <th scope="row" className="matrix__what">
                    {permissionLabel(permission)}
                    <span className="matrix__sub">
                      <code>{permission}</code>
                      {locked ? <span className="pill pill--muted">Owner only</span> : null}
                    </span>
                  </th>

                  {roles.map((role) => {
                    const granted = (draft?.[role.key] ?? role.permissions).includes(permission);
                    const name = `${role.label} — ${permissionLabel(permission)}`;

                    /* No checkbox at all where the answer is not ours to
                       change: the owner column, and every owner-only row.
                       A DISABLED checkbox would still read as a control, and
                       an empty one would read as an offer. */
                    if (role.key === 'owner' || locked) {
                      return (
                        <td key={role.key} className="matrix__locked">
                          <span aria-hidden="true">{granted ? '✓' : '—'}</span>
                          <span className="sr-only">
                            {name}:{' '}
                            {granted
                              ? 'granted, and locked'
                              : role.key === 'owner'
                                ? 'not granted, and locked'
                                : 'owner only'}
                          </span>
                        </td>
                      );
                    }

                    return (
                      <td key={role.key}>
                        <label className="matrix__cell">
                          <input
                            type="checkbox"
                            aria-label={name}
                            checked={granted}
                            disabled={busy}
                            onChange={() => onToggle(role.key, permission)}
                          />
                        </label>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        ))}
      </table>
    </div>
  );
}

/**
 * What the save did, named role by role.
 *
 * A run stops at the first refusal, so there are three things to say and they
 * are not interchangeable: everything landed, nothing landed, or some of it
 * did and here is the one that did not.
 */
function Outcome({
  outcome,
  error,
  labelOf,
}: {
  outcome: { saved: string[]; failed: { key: string; message: string } | null } | null;
  error: unknown;
  labelOf: (key: string) => string;
}) {
  if (error) {
    return (
      <p className="alert" role="alert">
        {error instanceof ApiError ? error.message : 'That did not work.'}
      </p>
    );
  }
  if (!outcome) return null;

  if (!outcome.failed) {
    return (
      <p className="alert alert--ok" role="status">
        Saved: {outcome.saved.map(labelOf).join(', ')}.
      </p>
    );
  }

  return (
    <p className="alert" role="alert">
      {outcome.saved.length > 0 ? `Saved: ${outcome.saved.map(labelOf).join(', ')}. ` : ''}
      {labelOf(outcome.failed.key)} was not saved — {outcome.failed.message}
    </p>
  );
}

/* --- a new role ------------------------------------------------------------ */

/**
 * A new role starts with NOTHING granted.
 *
 * Fail-closed, and the same rule the API follows for a new permission: the
 * owner grants what the role needs on the grid above, deliberately, rather
 * than starting from a copy of somebody else's access.
 */
function AddRole() {
  const create = useCreateRole();
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    create.mutate(
      { key: key.trim(), label: label.trim(), permissions: [] },
      {
        onSuccess: () => {
          setKey('');
          setLabel('');
        },
      },
    );
  }

  const fieldError = (name: string): string | undefined =>
    create.error instanceof ApiError ? create.error.fields[name] : undefined;

  return (
    <section className="card">
      <h2 className="section-title">Add a role</h2>

      <form className="stack" onSubmit={onSubmit} noValidate>
        <div className="field">
          <label htmlFor="roleLabel">Role name</label>
          <input
            id="roleLabel"
            value={label}
            maxLength={60}
            onChange={(event) => setLabel(event.target.value)}
          />
          {fieldError('label') ? (
            <span className="field__error" role="alert">
              {fieldError('label')}
            </span>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor="roleKey">Role key</label>
          <input
            id="roleKey"
            value={key}
            maxLength={40}
            onChange={(event) => setKey(event.target.value)}
          />
          {fieldError('key') ? (
            <span className="field__error" role="alert">
              {fieldError('key')}
            </span>
          ) : null}
          {/* Said before they type it: every account points at this string,
              so it is the one thing on this screen that cannot be corrected
              later. Lower-case letters, digits and dashes is what the API
              accepts. */}
          <span className="muted">
            Lower-case letters, digits and dashes — <code>night-shift</code>. Every account points
            at this, so it cannot be changed afterwards. The name above can.
          </span>
        </div>

        {create.error instanceof ApiError && Object.keys(create.error.fields).length === 0 ? (
          <p className="alert" role="alert">
            {create.error.message}
          </p>
        ) : null}

        <div className="row">
          <button
            className="btn"
            type="submit"
            disabled={create.isPending || !key.trim() || !label.trim()}
          >
            {create.isPending ? 'Adding…' : 'Add role'}
          </button>
          <span className="muted" role="status">
            {create.isSuccess && !create.isPending
              ? 'Added, with nothing granted. Tick what it needs above, then save.'
              : 'A new role starts with nothing granted.'}
          </span>
        </div>
      </form>
    </section>
  );
}
